import { useState, useEffect, useCallback, useRef } from "react";
import { OktaAuth } from "@okta/okta-auth-js";
import { requestJson, withTimeout } from "./api";

const OKTA_DOMAIN = process.env.REACT_APP_OKTA_DOMAIN;
const OKTA_CLIENT_ID = process.env.REACT_APP_OKTA_CLIENT_ID;
const OKTA_CONFIGURED = Boolean(OKTA_DOMAIN && OKTA_CLIENT_ID);

const oktaAuth = OKTA_CONFIGURED
  ? new OktaAuth({
      issuer: `https://${OKTA_DOMAIN}`,
      clientId: OKTA_CLIENT_ID,
      redirectUri: window.location.origin,
      scopes: ["openid", "profile", "email", "groups"],
      pkce: true,
      tokenManager: { storage: "sessionStorage" },
      autoRenew: true,
    })
  : null;

const BOARDS = [
  { id: "all-hands", label: "All Hands" },
];

const VOTED_KEY = (board) => `anonqa:voted:${board}`;

const MODERATOR_EMAILS = ["joshua@eigenlabs.org", "danielle@eigenlabs.org", "sarah@eigenlabs.org", "daren@eigenlabs.org"];

function isModerator(groups, email) {
  if (MODERATOR_EMAILS.includes(email)) return true;
  return Array.isArray(groups) && groups.includes("GRP-Execs");
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const accent = "#534AB7";
const accentLight = "#EEEDFE";

const REPO_URL = "https://github.com/Layr-Labs/eigenfloor";
const SAMPLE_ANON_ROW = `{
  "id":         "8f3a1c7e-2b9d-4e51-a0c6-7d5e9f10b2a4",
  "text":       "What is the average airspeed velocity of an unladen swallow?",
  "name":        null,
  "board":      "all-hands",
  "status":     "pending",
  "votes":      0,
  "answered":   false,
  "created_at": "2026-06-25T03:41:22Z"
}`;

function ExternalLink({ href, children, color }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       style={{ color: color || accent, fontWeight: 600, textDecoration: "none", borderBottom: `1px solid ${color || accent}40` }}>
      {children}
    </a>
  );
}

// ── Trust / "How this works" banner ──────────────────────────────────────────
// Collapsible panel explaining question storage and privacy.
// Rendered under the header. Defaults open on first visit, remembers collapse state.
function TrustBanner({ dark, surface, border, textPrimary, textMuted, bg }) {
  const STORE_KEY = "anonqa:trust:collapsed";
  const [open, setOpen] = useState(() => localStorage.getItem(STORE_KEY) !== "1");
  const toggle = () => {
    setOpen((o) => {
      localStorage.setItem(STORE_KEY, o ? "1" : "0");
      return !o;
    });
  };

  const Row = ({ icon, title, children }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 2, color: textPrimary }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: textMuted }}>{children}</div>
      </div>
    </div>
  );

  const shield = <svg width="15" height="15" viewBox="0 0 18 18" fill="none"><path d="M9 1.5l6 2.2v4.1c0 3.8-2.5 6.7-6 8.2-3.5-1.5-6-4.4-6-8.2V3.7L9 1.5z" stroke={accent} strokeWidth="1.3" strokeLinejoin="round"/><path d="M6.2 9l1.9 1.9L12 7" stroke={accent} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
  const mask  = <svg width="15" height="15" viewBox="0 0 18 18" fill="none"><path d="M2.5 6.5c2-1 4-1.5 6.5-1.5s4.5.5 6.5 1.5c0 4-2.5 6.5-6.5 6.5S2.5 10.5 2.5 6.5z" stroke={accent} strokeWidth="1.3" strokeLinejoin="round"/><circle cx="6.5" cy="8" r="1" fill={accent}/><circle cx="11.5" cy="8" r="1" fill={accent}/></svg>;
  const lock  = <svg width="15" height="15" viewBox="0 0 18 18" fill="none"><rect x="4" y="8" width="10" height="7" rx="1.5" stroke={accent} strokeWidth="1.3"/><path d="M6 8V6a3 3 0 016 0v2" stroke={accent} strokeWidth="1.3" strokeLinecap="round"/></svg>;
  const code  = <svg width="15" height="15" viewBox="0 0 18 18" fill="none"><path d="M6.5 6L3.5 9l3 3M11.5 6l3 3-3 3" stroke={accent} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;

  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 14, background: surface, marginBottom: "1.5rem", overflow: "hidden" }}>
      <button onClick={toggle} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 16px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ display: "inline-flex" }}>{shield}</span>
          <span style={{ fontWeight: 600, fontSize: 13.5, color: textPrimary }}>Anonymous questions · how your data is handled</span>
        </span>
        <span style={{ fontSize: 12, color: textMuted, whiteSpace: "nowrap" }}>{open ? "Hide details ▲" : "How this works ▾"}</span>
      </button>

      {open && (
        <div style={{ padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
          <Row icon={shield} title="Privacy and its limits">
            Questions are stored in a database hosted by Neon; the app runs on Vercel. Platform administrators can access stored data and may have access to request metadata. This is not a guarantee against identification by administrators, timing, or details you include in your question.
          </Row>

          <Row icon={mask} title="Leave the name blank to post anonymously">
            If you don't type a name, the <code style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>name</code> field is saved as <code style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>null</code>. The question row contains no login, email, IP address, or device fingerprint. Optional AI moderation sends the question text to Anthropic, so avoid including identifying details.
          </Row>

          <Row icon={lock} title="Sign-in keeps it to the team — not the public internet">
            Access is gated through Okta single sign-on, so only verified members of the organization can read or post. This keeps out random outsiders and spam while still letting you stay anonymous <em>within</em> the team — auth proves you belong here, it does not tag your question.
          </Row>

          <Row icon={code} title="Review the source code">
            The application source is available in the repository to people with access. {" "}
            <ExternalLink href={REPO_URL}>github.com/Layr-Labs/eigenfloor →</ExternalLink>
          </Row>

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Illustrative anonymous database entry</div>
            <pre style={{ margin: 0, background: dark ? "#15152b" : "#f3f2fb", border: `1px solid ${border}`, borderRadius: 10, padding: "12px 14px", fontSize: 12, lineHeight: 1.5, color: textPrimary, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", overflowX: "auto", whiteSpace: "pre" }}>{SAMPLE_ANON_ROW}</pre>
            <div style={{ fontSize: 11.5, color: textMuted, marginTop: 6, lineHeight: 1.5 }}>The app does not add your sign-in identity to this row. Hosting and authentication services handle sign-in and request metadata separately.</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [dark, setDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [authAttempt, setAuthAttempt] = useState(0);
  const [user, setUser] = useState(null); // { name, email, groups }
  const [activeBoard, setActiveBoard] = useState("all-hands");
  const [view, setView] = useState("submit");
  const [questions, setQuestions] = useState([]);
  const [settings, setSettings] = useState({ slackWebhook: "", reminderDays: ["Monday", "Wednesday"], channelName: "#all-hands-questions", eventName: "All Hands Q&A" });
  const [submitted, setSubmitted] = useState(false);
  const [qText, setQText] = useState("");
  const [qName, setQName] = useState("");
  const [qError, setQError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [adminFilter, setAdminFilter] = useState("pending");
  const [voted, setVoted] = useState([]);
  const handlingCallback = useRef(false);

  // ── Dark mode listener ─────────────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // ── Auth init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!oktaAuth) { setAuthLoading(false); return; }
      setAuthLoading(true);
      setAuthError("");
      try {
        await withTimeout(async () => {
        if (oktaAuth.isLoginRedirect()) {
          if (!handlingCallback.current) {
            handlingCallback.current = true;
            await oktaAuth.handleLoginRedirect();
          }
        }
        const idToken = await oktaAuth.tokenManager.get("idToken");
        const accessToken = await oktaAuth.tokenManager.get("accessToken");
        if (idToken && accessToken) {
          const claims = idToken.claims;
          if (!cancelled) setUser({
            name: claims.name || claims.email || claims.sub,
            email: claims.email || claims.sub,
            groups: Array.isArray(claims.groups) ? claims.groups : [],
          });
        } else {
          await oktaAuth.signInWithRedirect();
          throw new Error("Sign-in redirect did not complete.");
        }
        }, 15000);
      } catch {
        handlingCallback.current = false;
        if (!cancelled) setAuthError("Sign-in could not complete. Please try again.");
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authAttempt]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  async function getToken() {
    try {
      const token = await withTimeout(() => oktaAuth.tokenManager.get("accessToken"), 5000);
      return token?.accessToken ?? null;
    } catch {
      return null;
    }
  }

  async function authFetch(path, options = {}) {
    const token = await getToken();
    if (!token) {
      setAuthError("Your session has expired. Please sign in again.");
      throw new Error("Sign-in required.");
    }
    try {
      return await requestJson(path, token, options);
    } catch (error) {
      if (error.status === 401) setAuthError(error.message);
      throw error;
    }
  }

  const api = {
    get: async (path) => {
      return authFetch(path);
    },
    post: async (path, body) => {
      return authFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    patch: async (id, body) => {
      try { return await authFetch(`/api/questions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }); } catch { return { error: "Action failed. Please try again." }; }
    },
    del: async (id) => {
      try { return await authFetch(`/api/questions/${id}`, { method: "DELETE" }); }
      catch { return { error: "Failed to delete. Please try again." }; }
    },
  };

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadQuestions = useCallback(async (board, mod) => {
    try {
      const data = await api.get(`/api/questions?board=${board}`);
      if (!Array.isArray(data)) throw new Error("Invalid response.");
      setQuestions(data);
      setLoadError("");
    } catch {
      setLoadError("Questions could not be loaded. Please try again.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = useCallback(async (board) => {
    try {
      const data = await api.get(`/api/settings?board=${board}`);
      if (data && !data.error) setSettings((s) => ({ ...s, ...data }));
    } catch { showToast("Settings could not be loaded. Please try again."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    (async () => {
      setLoading(true);
      try { await loadQuestions(activeBoard, isModerator(user.groups, user.email)); }
      finally { setLoading(false); }
    })();
    try { setVoted(JSON.parse(localStorage.getItem(VOTED_KEY(activeBoard)) || "[]")); } catch {}
  }, [authLoading, user, activeBoard, loadQuestions, loadAttempt]);

  function switchBoard(board) {
    setActiveBoard(board);
    setView("submit");
    setSubmitted(false);
    setAdminFilter("pending");
    try { setVoted(JSON.parse(localStorage.getItem(VOTED_KEY(board)) || "[]")); } catch {}
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  async function submitQuestion() {
    if (!qText.trim()) { setQError("Please enter a question."); return; }
    if (qText.trim().length < 10) { setQError("A bit short — can you add more detail?"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/api/questions", { text: qText.trim(), name: qName.trim() || null, board: activeBoard });
      if (!res || res.error) { showToast("Something went wrong. Please try again."); return; }
      setSubmitted(true); setQText(""); setQName("");
    } catch { showToast("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  }

  async function upvote(id) {
    if (voted.includes(id)) return;
    const newVoted = [...voted, id];
    setVoted(newVoted);
    localStorage.setItem(VOTED_KEY(activeBoard), JSON.stringify(newVoted));
    setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, votes: (q.votes || 0) + 1 } : q));
    const result = await api.patch(id, { action: "upvote" });
    if (!result || result.error) {
      setVoted(voted);
      localStorage.setItem(VOTED_KEY(activeBoard), JSON.stringify(voted));
      showToast("Vote could not be saved. Please try again.");
      await loadQuestions(activeBoard);
    }
  }

  async function updateStatus(id, status) {
    const result = await api.patch(id, { action: status });
    if (!result || result.error) { showToast(result?.error || "Action failed"); return; }
    await loadQuestions(activeBoard, isModerator(user?.groups, user?.email));
  }

  async function toggleAnswered(id) {
    const result = await api.patch(id, { action: "toggleAnswered" });
    if (!result || result.error) { showToast(result?.error || "Action failed"); return; }
    setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, answered: !q.answered } : q));
  }

  async function deleteQ(id) {
    const res = await api.del(id);
    if (!res || res.error || !res.ok) {
      showToast(res?.error || "Failed to delete.");
      return;
    }
    setQuestions((qs) => qs.filter((q) => q.id !== id));
  }

  async function saveSettings() {
    try {
      await api.post(`/api/settings?board=${activeBoard}`, settings);
      showToast("Settings saved!");
    } catch { showToast("Settings could not be saved. Please try again."); }
  }

  async function sendToSlack() {
    if (!settings.slackWebhook) { showToast("No webhook configured — check Settings."); return; }
    const top = questions.filter((q) => q.status === "approved" && !q.answered).sort((a, b) => (b.votes || 0) - (a.votes || 0));
    if (!top.length) { showToast("No approved unanswered questions to send."); return; }
    const boardLabel = BOARDS.find((b) => b.id === activeBoard)?.label || activeBoard;
    const lines = top.map((q, i) => `${i + 1}. ${q.text}${q.votes > 0 ? ` _(${q.votes} +1${q.votes !== 1 ? "s" : ""})_` : ""}${q.name ? ` — _${q.name}_` : ""}`).join("\n");
    try {
      await withTimeout((signal) => fetch(settings.slackWebhook, { signal, method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: `*${settings.eventName} (${boardLabel}) — Top Questions*\n\n${lines}` }) }));
      showToast("Sent to Slack!");
    } catch { showToast("Failed — check your webhook URL."); }
  }

  function exportCopy() {
    const qs = questions.filter((q) => q.status === "approved" && !q.answered).sort((a, b) => (b.votes || 0) - (a.votes || 0));
    navigator.clipboard.writeText(qs.map((q, i) => `${i + 1}. ${q.text}${q.votes > 0 ? ` (+${q.votes})` : ""}`).join("\n\n"));
    showToast("Copied to clipboard!");
  }

  function downloadTxt() {
    const qs = questions.filter((q) => q.status === "approved" && !q.answered).sort((a, b) => (b.votes || 0) - (a.votes || 0));
    const text = qs.map((q, i) => `${i + 1}. ${q.text}${q.votes > 0 ? ` (+${q.votes})` : ""}`).join("\n\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = `questions-${activeBoard}-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
  }

  // ── Derived state ──────────────────────────────────────────────────────────
  const canModerate = user ? isModerator(user.groups, user.email) : false;
  const pending  = questions.filter((q) => q.status === "pending");
  const approved = questions.filter((q) => q.status === "approved" && !q.answered).sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const archived = questions.filter((q) => q.status === "approved" && q.answered).sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const boardQuestions = questions.filter((q) => q.status === "approved" && !q.answered).sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const rejected = questions.filter((q) => q.status === "rejected");
  const filtered = adminFilter === "pending" ? pending : adminFilter === "approved" ? approved : adminFilter === "archive" ? archived : rejected;

  // ── Styles ─────────────────────────────────────────────────────────────────
  const bg = dark ? "#1a1a2e" : "#ffffff";
  const surface = dark ? "#252540" : "#f9fafb";
  const border = dark ? "#3a3a5c" : "#e5e7eb";
  const textPrimary = dark ? "#f1f1f4" : "#111827";
  const textMuted = dark ? "#9ca3b0" : "#9ca3af";

  const primaryBtn = { padding: "9px 22px", borderRadius: 99, border: "none", background: accent, color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" };
  const outlineBtn = { padding: "6px 14px", borderRadius: 99, fontSize: 13, cursor: "pointer", border: `1px solid ${border}`, background: "transparent", color: textPrimary, fontFamily: "inherit" };
  const ghostBtn   = { padding: "4px 10px", borderRadius: 8, fontSize: 12, cursor: "pointer", border: `1px solid ${border}`, background: "transparent", color: textMuted, fontFamily: "inherit" };
  const inputStyle = { width: "100%", boxSizing: "border-box", borderRadius: 10, border: `1px solid ${border}`, padding: "10px 14px", fontSize: 14, background: surface, fontFamily: "inherit", outline: "none", color: textPrimary };

  function Pill({ label, active, onClick }) {
    return (
      <button onClick={onClick} style={{ padding: "5px 14px", borderRadius: 99, fontSize: 13, cursor: "pointer", border: `1px solid ${active ? accent : border}`, background: active ? accentLight : "transparent", color: active ? "#3C3489" : textMuted, fontFamily: "inherit", fontWeight: active ? 600 : 400 }}>
        {label}
      </button>
    );
  }

  // ── Loading / auth states ──────────────────────────────────────────────────
  if (!OKTA_CONFIGURED) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: "#ef4444", fontSize: 14, padding: "2rem", textAlign: "center" }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Configuration Error</div>
          <div>Missing required environment variables: REACT_APP_OKTA_DOMAIN and/or REACT_APP_OKTA_CLIENT_ID.</div>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: textMuted, fontSize: 14 }}>Signing in…</div>;
  }

  if (authError) {
    return <div role="alert" style={{ padding: "3rem", textAlign: "center", color: textPrimary }}>
      <p>{authError}</p>
      <button style={primaryBtn} onClick={() => { oktaAuth.tokenManager.clear(); setAuthAttempt((attempt) => attempt + 1); }}>Try signing in again</button>
    </div>;
  }

  if (!user) return null; // signing in redirect in progress

  if (loading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: textMuted, fontSize: 14 }}>Loading…</div>;
  }

  if (loadError) {
    return <div role="alert" style={{ padding: "3rem", textAlign: "center", color: textPrimary }}>
      <p>{loadError}</p>
      <button style={primaryBtn} onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Retry loading questions</button>
    </div>;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "2rem 1rem", fontFamily: "system-ui, -apple-system, sans-serif", color: textPrimary, background: bg, minHeight: "100vh" }}>
      {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: "#26215C", color: "#EEEDFE", padding: "9px 20px", borderRadius: 99, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", zIndex: 999 }}>{toast}</div>}

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9c0 1.29.33 2.5.91 3.55L1.5 16.5l3.95-.91A7.5 7.5 0 1 0 9 1.5z" stroke={accent} strokeWidth="1.4" strokeLinejoin="round"/><circle cx="6" cy="9" r="1" fill={accent}/><circle cx="9" cy="9" r="1" fill={accent}/><circle cx="12" cy="9" r="1" fill={accent}/></svg>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>Anonymous Q&A</div>
            <div style={{ fontSize: 12, color: textMuted }}>Ask anything. No judgment.</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: textMuted }}>{user.name}</span>
          <button onClick={() => oktaAuth.signOut()} style={{ ...outlineBtn, color: textMuted, fontSize: 12 }}>Sign out</button>
        </div>
      </div>

      {/* ── Trust / transparency banner ── */}
      <TrustBanner dark={dark} surface={surface} border={border} textPrimary={textPrimary} textMuted={textMuted} bg={bg} />

      {/* ── Board selector (hidden when single board) ── */}
      {BOARDS.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginBottom: "1.25rem", flexWrap: "wrap" }}>
          {BOARDS.map((b) => (
            <button
              key={b.id}
              onClick={() => switchBoard(b.id)}
              style={{ padding: "5px 14px", borderRadius: 99, fontSize: 13, cursor: "pointer", border: `1px solid ${activeBoard === b.id ? accent : border}`, background: activeBoard === b.id ? accentLight : "transparent", color: activeBoard === b.id ? "#3C3489" : textMuted, fontFamily: "inherit", fontWeight: activeBoard === b.id ? 600 : 400 }}
            >
              {b.label}
            </button>
          ))}
        </div>
      )}

      {/* ── View nav ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.75rem", flexWrap: "wrap", alignItems: "center" }}>
        <Pill label="Ask a question" active={view === "submit"} onClick={() => setView("submit")} />
        <Pill label={`Board (${boardQuestions.length})`} active={view === "board"} onClick={() => { setView("board"); loadQuestions(activeBoard, canModerate); }} />
        {canModerate && (
          <Pill
            label={`Admin${pending.length > 0 ? ` (${pending.length})` : ""}`}
            active={view === "admin" || view === "settings"}
            onClick={() => { setView("admin"); loadSettings(activeBoard); }}
          />
        )}
      </div>

      {/* ── Submit view ── */}
      {view === "submit" && !submitted && (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, padding: "1.5rem" }}>
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Your question</div>
            <textarea value={qText} onChange={(e) => { setQText(e.target.value); setQError(""); }} placeholder="What would you like to ask?" maxLength={500} style={{ ...inputStyle, resize: "vertical", minHeight: 100, fontSize: 15, lineHeight: 1.6 }} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {qError ? <span style={{ fontSize: 12, color: "#ef4444" }}>{qError}</span> : <span />}
              <span style={{ fontSize: 12, color: textMuted }}>{qText.length}/500</span>
            </div>
          </div>
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Name <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— optional</span></div>
            <input value={qName} onChange={(e) => setQName(e.target.value)} placeholder="Leave blank to stay anonymous" maxLength={60} style={{ ...inputStyle, width: "55%" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, color: textMuted, lineHeight: 1.5 }}>Questions are reviewed before<br />appearing on the public board.</div>
            <button onClick={submitQuestion} disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? "Submitting…" : qName.trim() ? "Submit question" : "Submit anonymously"}</button>
          </div>
        </div>
      )}

      {view === "submit" && submitted && (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, padding: "3rem 1.5rem", textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: accentLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4.5 11.5l5 5 8-9" stroke={accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Question submitted</div>
          <div style={{ fontSize: 14, color: textMuted, marginBottom: "1.5rem" }}>Once approved it'll appear on the board for others to +1.</div>
          <button onClick={() => setSubmitted(false)} style={outlineBtn}>Ask another question</button>
        </div>
      )}

      {/* ── Board view ── */}
      {view === "board" && (
        <>
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{BOARDS.find((b) => b.id === activeBoard)?.label} — Questions</div>
            <div style={{ fontSize: 13, color: textMuted }}>+1 the questions you most want answered. Sorted by votes.</div>
          </div>
          {boardQuestions.length === 0 && <div style={{ background: surface, borderRadius: 12, padding: "2.5rem", textAlign: "center", color: textMuted, fontSize: 14 }}>No approved questions yet — check back soon.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {boardQuestions.map((q, i) => {
              const hasVoted = voted.includes(q.id);
              return (
                <div key={q.id} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: "1rem 1.25rem", display: "flex", gap: 16 }}>
                  <button onClick={() => upvote(q.id)} disabled={hasVoted} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 40, background: hasVoted ? accentLight : surface, border: `1px solid ${hasVoted ? accent : border}`, borderRadius: 10, padding: "8px 6px", cursor: hasVoted ? "default" : "pointer" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2.5L11.5 8H2.5L7 2.5Z" fill={hasVoted ? accent : textMuted}/></svg>
                    <span style={{ fontSize: 14, fontWeight: 600, color: hasVoted ? accent : textPrimary, lineHeight: 1 }}>{q.votes || 0}</span>
                  </button>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: "0 0 6px", fontSize: 15, lineHeight: 1.6 }}>{q.text}</p>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {q.name && <span style={{ fontSize: 12, color: textMuted }}>from {q.name}</span>}
                      <span style={{ fontSize: 12, color: textMuted }}>{timeAgo(q.created_at)}</span>
                      {i === 0 && boardQuestions.length > 1 && !q.answered && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: accentLight, color: "#3C3489", fontWeight: 500 }}>top question</span>}
                      {q.answered && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#d1fae5", color: "#065f46", fontWeight: 500 }}>answered</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Admin view ── */}
      {view === "admin" && canModerate && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap", alignItems: "center" }}>
            {[["pending", `Pending (${pending.length})`], ["approved", `Approved (${approved.length})`], ["archive", `Archive (${archived.length})`], ["rejected", `Rejected (${rejected.length})`]].map(([f, label]) => (
              <Pill key={f} label={label} active={adminFilter === f} onClick={() => setAdminFilter(f)} />
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button onClick={() => { setView("settings"); loadSettings(activeBoard); }} style={outlineBtn}>Settings</button>
              <button onClick={sendToSlack} style={outlineBtn}>Send to Slack</button>
              <button onClick={exportCopy} style={outlineBtn}>Copy</button>
              <button onClick={downloadTxt} style={outlineBtn}>Download</button>
            </div>
          </div>
          {filtered.length === 0 && <div style={{ background: surface, borderRadius: 12, padding: "2.5rem", textAlign: "center", color: textMuted, fontSize: 14 }}>Nothing here.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map((q) => (
              <div key={q.id} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: "1rem 1.25rem" }}>
                <p style={{ margin: "0 0 8px", fontSize: 15, lineHeight: 1.6 }}>{q.text}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: textMuted }}>{timeAgo(q.created_at)}</span>
                  {q.name && <span style={{ fontSize: 12, color: textMuted }}>· {q.name}</span>}
                  {q.status === "approved" && <span style={{ fontSize: 12, color: textMuted }}>· {q.votes || 0} vote{q.votes !== 1 ? "s" : ""}</span>}
                  {q.answered && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#d1fae5", color: "#065f46", fontWeight: 500 }}>answered</span>}
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    {q.status === "pending" && <><button onClick={() => updateStatus(q.id, "approve")} style={{ ...ghostBtn, color: "#059669", borderColor: "#6ee7b7" }}>Approve</button><button onClick={() => updateStatus(q.id, "reject")} style={{ ...ghostBtn, color: "#ef4444", borderColor: "#fca5a5" }}>Reject</button></>}
                    {q.status === "approved" && <><button onClick={() => toggleAnswered(q.id)} style={ghostBtn}>{q.answered ? "Unmark" : "Mark answered"}</button><button onClick={() => updateStatus(q.id, "pending")} style={ghostBtn}>Move to pending</button></>}
                    {q.status === "rejected" && <button onClick={() => updateStatus(q.id, "pending")} style={ghostBtn}>Restore</button>}
                    <button onClick={() => deleteQ(q.id)} style={{ ...ghostBtn, color: "#ef4444" }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Settings view ── */}
      {view === "settings" && canModerate && (
        <div>
          <button onClick={() => setView("admin")} style={{ ...outlineBtn, marginBottom: "1.25rem" }}>← Back</button>
          <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, padding: "1.5rem" }}>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: "1.25rem" }}>Slack integration — {BOARDS.find((b) => b.id === activeBoard)?.label}</div>
            <label style={{ fontSize: 13, color: textMuted, display: "block", marginBottom: 6 }}>Incoming webhook URL</label>
            <input type="url" value={settings.slackWebhook} onChange={(e) => setSettings({ ...settings, slackWebhook: e.target.value })} placeholder="https://hooks.slack.com/services/..." style={{ ...inputStyle, marginBottom: 14 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {[["Event name", "eventName"], ["Slack channel", "channelName"]].map(([label, key]) => (
                <div key={key}>
                  <label style={{ fontSize: 13, color: textMuted, display: "block", marginBottom: 6 }}>{label}</label>
                  <input value={settings[key]} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} style={inputStyle} />
                </div>
              ))}
            </div>
            <label style={{ fontSize: 13, color: textMuted, display: "block", marginBottom: 8 }}>Reminder days</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {["Monday","Tuesday","Wednesday","Thursday","Friday"].map((d) => {
                const on = settings.reminderDays.includes(d);
                return <button key={d} onClick={() => setSettings({ ...settings, reminderDays: on ? settings.reminderDays.filter((x) => x !== d) : [...settings.reminderDays, d] })} style={{ padding: "5px 12px", borderRadius: 99, fontSize: 13, cursor: "pointer", border: `1px solid ${on ? accent : border}`, background: on ? accentLight : "transparent", color: on ? "#3C3489" : textMuted, fontFamily: "inherit", fontWeight: on ? 600 : 400 }}>{d}</button>;
              })}
            </div>
            <div style={{ fontSize: 12, color: textMuted, marginBottom: "1.25rem", padding: "10px 12px", background: surface, borderRadius: 8 }}>
              <code style={{ fontSize: 11 }}>/remind #channel "Submit questions: [link]" every {settings.reminderDays.join(", ")} at 9am</code>
            </div>
            <button onClick={saveSettings} style={primaryBtn}>Save settings</button>
          </div>
        </div>
      )}
    </div>
  );
}
